/**
 * interior_wiring.ts — THE PLANNER IN THE VISIT, as rules (the interiors programme, slice C, 2026-10-01). PURE: no
 * three, no DOM, no rows — the visit (`BuildingInteriorModule`), the resident mirror (`EnvironmentModule` through
 * `home_layout` / `deed_home`), the structure console, the E arbiter (`FurnitureUseModule`) and the gate
 * (`verify:interior-wiring`) all read the SAME functions.
 *
 *   THE LEVER       `localStorage atlas:interior-planner = 'off'` → today's furnishers, everywhere (the five functions
 *                   `BuildingInteriorModule` has always dispatched to — `legacyFurnisherFor` names the dispatch).
 *   THE LAYOUT      a deed is planned at ITS PIN (`structure_layout`): NO ROW = layout 0 = today's legacy plan, so every
 *                   pack that existed before the pin keeps its room and its storage keys byte for byte; revision ≥ 1 =
 *                   the planner at that revision. Everything that is not a deed has no pin and takes the current one.
 *   THE ROUTE       which furnisher draws a room (`furnishRouteFor`): the descriptor — except the guild hall and the
 *                   Hive store while their archetype is still `legacy.*` (their bespoke furnishers know the shell's
 *                   columns and casework; `purposePlan` does not).
 *   THE PLAN CACHE  `planCached`: one plan per (building, shell, purpose, tradition, layout) for the session, LRU under
 *                   a BYTE budget (a tower's descriptor is hundreds of kilobytes; a house's a few).
 *   THE SEAT VIEW   what the visit seats, in order: the descriptor's generated pieces (a deed: its PACK's prefix —
 *                   `packView`), each with its storage key, its height, whether the detail level shows it and whether
 *                   it gets a collider.
 *   THE DETAIL RULE `pieceShownAtDetail`: a render filter, never a plan option (the plan, the digest and every pack rank
 *                   are the same at every level).
 *   THE RESIDENTS   `residentRoomOf`: the sockets, standing points and rugs residents take (`home_layout.anchoredHomeSpot`).
 *   THE ACTIVE DESCRIPTOR  `activeInterior()` — what the room the player stands in IS (globalThis-backed: the
 *                   instance-split law). Later slices read it: units, floor links, the lift candidate, cover.
 *
 * IDENTITY BEFORE BEAUTY. A pinned room is its PIN: the revision and the digest the owner's client stamped. Since C2
 * (2026-10-01) every namespace plans with the LOOK's resolved tradition (`styleFor(look).tradition` — a deed's city
 * alignment, a player civilisation's chosen tradition; slice D subscribes the rows with the visit), and the pin's
 * digest COVERS it: a pinned room whose city later changes tradition is still found by its digest (the visit tries the
 * resolved tradition, then the others — `pinnedTraditionOrder`), drawn exactly as pinned, flagged as drift, and moves
 * only when its owner asks for a Refit. Layout 0 (no pin row) takes NO tradition: today's legacy plan and today's
 * storage keys, byte for byte. NO owned pieces ever (a chair the owner places must never re-rank a chest).
 *
 * THE DRIFT POLICY (C2). `CURRENT_LAYOUT_REVISION` is bumped ONCE PER RELEASE that moves planned rooms, never per
 * edit. Under a pin, a room whose digest no longer matches (a planner / kit / catalog change inside one revision, or a
 * tradition change) keeps being drawn — as pinned when a tradition still reproduces the digest, else as today's plan
 * at its revision — says so ONCE in the console, and offers Refit. Its storage is never orphaned: a container whose
 * piece the room no longer seats is listed by the console and opens from there.
 *
 * THE LIVE STORAGE (C2). The server lets one structure hold 16 pack containers, 6 of a kind, and 6 growing planters;
 * a planned museum seats 22 bookcases. `liveStorageKeys` marks, from the descriptor alone, WHICH storage pieces are
 * storage (smallest pack first, then floor, then order — so a bigger pack never moves the mark); the rest are plain
 * furniture with no storage prompt at all. `keyedOffer` is the E arbiter's whole rule.
 */
import { FLOOR_H, setLiftShaftRuleLive } from './building_interiors.js';
import { FURNITURE } from './furniture_catalog.js';
import { FURNITURE_FUNCTION } from './furniture_functions.js';
import { doorSignSpan, SIGN_W } from './interior_plans.js';
import { FURNISH_PACKS, type FurnishPack } from './furnish_packs.js';
import type { InteriorPurpose } from './interior_purpose.js';
import { planInterior, packView, type PlanInput, type PlanResult } from './interior_planner.js';
import { pieceMeta, KIT_VERSION } from './interior_kits.js';
import { archetypeFor } from './interior_archetypes.js';
import { pieceCorners, type DescAnchor, type DescFloor, type DescPiece, type InteriorDescriptor, type PieceTier, type PlanReport } from './interior_descriptor.js';
import { ROOMS_LAYOUT_REVISION, partitionBoxes, roomAt, roomIdOf } from './interior_descriptor.js'; // (slice G — its own line)
import { deedBoxAgrees, deedPlanBox } from './interior_rooms.js';
import type { ResidentRoom } from './home_layout.js';
import { INTERIOR_DETAIL_RULES, interiorLookEnabled } from './interior_style.js';
import { isKitProp, kitProp, propBounds, type FixtureOptions } from './interior_kit_props.js';

// ─── The lever ───────────────────────────────────────────────────────────────────────────────────────────────────────

export const PLANNER_LEVER_KEY = 'atlas:interior-planner';
/** `localStorage atlas:interior-planner = 'off'` → today's furnishers, resident spots, pack counts and E offers. */
export function interiorPlannerEnabled(read?: (k: string) => string | null): boolean {
  try {
    const get = read ?? ((k: string) => (typeof localStorage === 'undefined' ? null : localStorage.getItem(k)));
    return get(PLANNER_LEVER_KEY) !== 'off';
  } catch { return true; }
}

// ─── The door's budget (slice H, 2026-10-02) ───────────────────────────────────────────────────────────────────────
// Three levers. Nothing they gate removes anything from a room: they move WHEN work happens (before E, nearest storey
// first) and how many draws the shell costs.
//
// MEASURED (impl-polish.md CHECKPOINT 2; the budget bed, cold, medians of three): the prompt's prewarm takes a cold door
// 2,053 → 830 ms (cantina), 2,298 → 1,165 (hotel), 3,855 → 2,701 (8-storey tower); the merged shell takes a room's
// draws 3–4× down (tower 944 → 247) with the same picture; streaming shows the first piece at once (tower 3,663 → 76
// ms) and leaves completion where it was. Each ON by default; `= 'off'` restores the visit of 2026-10-01.

const leverOn = (key: string, read?: (k: string) => string | null): boolean => {
  try {
    const get = read ?? ((k: string) => (typeof localStorage === 'undefined' ? null : localStorage.getItem(k)));
    return get(key) !== 'off';
  } catch { return true; }
};
/** `atlas:interior-prewarm = 'off'` → nothing is planned or loaded before E (the door prompt only prompts). */
export const PREWARM_LEVER_KEY = 'atlas:interior-prewarm';
export const interiorPrewarmEnabled = (read?: (k: string) => string | null): boolean => leverOn(PREWARM_LEVER_KEY, read);
/** `atlas:interior-stream = 'off'` → the plan's own order, one awaited piece after another, asks top-down. */
export const STREAM_LEVER_KEY = 'atlas:interior-stream';
export const interiorStreamEnabled = (read?: (k: string) => string | null): boolean => leverOn(STREAM_LEVER_KEY, read);
/** `atlas:interior-merge = 'off'` → one mesh per shell box and per window pane (the draw count of 2026-10-01). */
export const MERGE_LEVER_KEY = 'atlas:interior-merge';
export const interiorMergeEnabled = (read?: (k: string) => string | null): boolean => leverOn(MERGE_LEVER_KEY, read);

/** How long the door prompt must stand on one door before its room is prewarmed (a walk past a row of doors asks
 *  for nothing), and how long a prewarm's asset handles outlive the prompt (a step back and forth must not thrash). */
export const PREWARM_DWELL_MS = 150;
export const PREWARM_KEEP_MS = 8000;
/** Storeys within this many floors of the one you stand on are "near": seated first, and the `near` mark's set. */
export const NEAR_STOREYS = 1;

/** The storey a body at `feetAbove` metres over the building's ground stands on (the slab rule every floor band uses). */
export function storeyAt(feetAbove: number, floors: number, floorH: number): number {
  const f = Math.floor((feetAbove + 0.5) / floorH);
  return Math.max(0, Math.min(Math.max(1, floors) - 1, Number.isFinite(f) ? f : 0));
}

/**
 * THE STOREY YOU STAND ON FIRST. Every piece the view SHOWS, nearest storey first (then the lower storey, then the
 * plan's own order): the order the visit seats in. The far storeys follow — behind floor slabs, where a piece landing
 * late cannot be seen landing. The set is exactly the plan-order loop's (`shown` pieces), only the order differs.
 */
export function seatOrder(view: SeatView, here: number): SeatPiece[] {
  const out: SeatPiece[] = [];
  for (const fl of view.floors) for (const sp of fl.pieces) if (sp.shown) out.push(sp);
  const rank = new Map<SeatPiece, number>();
  out.forEach((sp, i) => rank.set(sp, i));
  return out.sort((a, b) => Math.abs(a.floor - here) - Math.abs(b.floor - here) || a.floor - b.floor || rank.get(a)! - rank.get(b)!);
}

/**
 * THE ORDER OF THE ASKS. The asset gate serves urgent asks last-in-first-out once its lanes are busy, and starts an
 * ask at once while a lane is free. So: the `freeLanes` nearest models are asked first (they start now), and the rest
 * are asked FARTHEST first — the nearest of them ends at the front of the queue. A model counts as near as the nearest
 * storey that seats it (the old top-down walk asked a model the ground floor shares with any upper storey first, and
 * so served it last). Distinct drawable (non-prop) keys of shown pieces only.
 */
export function prefetchOrder(view: SeatView, here: number, freeLanes: number): string[] {
  const dist = new Map<string, number>();
  for (const sp of seatOrder(view, here)) {
    if (isKitProp(sp.key)) continue;
    const d = Math.abs(sp.floor - here);
    if (!dist.has(sp.key)) dist.set(sp.key, d); // seatOrder is nearest first: the first sight of a key is its nearest
  }
  const near = [...dist.keys()]; // insertion order = nearest first
  const n = Math.max(0, Math.min(near.length, Math.floor(Number.isFinite(freeLanes) ? freeLanes : 0)));
  return [...near.slice(0, n), ...near.slice(n).reverse()];
}

// ─── The layout pin ──────────────────────────────────────────────────────────────────────────────────────────────────

/** The layout a NEW purchase or a Refit takes — a MIRROR of the server's `LAYOUT_REVISION_CURRENT`
 *  (`server/src/reducers/structures.ts`; `verify:interior-wiring` pins the two). 0 is today's legacy plan.
 *  THE DRIFT POLICY: bumped ONCE PER RELEASE that moves planned rooms — never per edit (see the header). */
export const CURRENT_LAYOUT_REVISION = 3; // 3 = THE STOREYS REVISION (slice U, 2026-10-02): a program on every storey, THE CLEAR-LANDING RULE (interior_descriptor.ts STOREYS_LAYOUT_REVISION); 2 = THE ROOMS REVISION (slice G)
/** THE STOREYS LEVER (slice U): `localStorage['atlas:interior-storeys'] = 'off'` (read once, at load — a reload applies
 *  it) plans every building that has no pin at THE ROOMS REVISION (layout 2) and cuts every non-deed shell with the
 *  classic shaft rule again — today's rooms before slice U. A pin keeps the layout it carries either way. */
export const STOREYS_LEVER_KEY = 'atlas:interior-storeys';
export function interiorStoreysEnabled(read?: (k: string) => string | null): boolean {
  try {
    const get = read ?? ((k: string) => (typeof localStorage === 'undefined' ? null : localStorage.getItem(k)));
    return get(STOREYS_LEVER_KEY) !== 'off';
  } catch { return true; }
}
let storeysOn = interiorStoreysEnabled();
setLiftShaftRuleLive(storeysOn ? null : 'classic');
/** Flip THE STOREYS LEVER in this page (harnesses and probes): the layout and the shell's rule move together. */
export function setInteriorStoreys(on: boolean): void { storeysOn = on; setLiftShaftRuleLive(on ? null : 'classic'); }
/** The layout a building WITHOUT a pin is planned at: the current revision, or the rooms revision under the lever. */
export function currentLayoutRevision(): number { return storeysOn ? CURRENT_LAYOUT_REVISION : Math.min(CURRENT_LAYOUT_REVISION, ROOMS_LAYOUT_REVISION); }
/** The server flag behind the stamp at purchase and both pin reducers (absent = ON). */
export const LAYOUT_PIN_FLAG = 'interior_layout_pin_v1';
/** A digest as the pin stores it. */
export const DIGEST_RE = /^[0-9a-f]{32}$/;

export interface LayoutPin { layoutRevision: number; digest: string }

/** Is this enterable a PACK interior (a player structure furnished by a bought pack)? The guild hall furnishes itself (law 44). */
export function isPackInterior(id: string, key: string): boolean {
  return id.startsWith('deed:') && key !== 'deed:guild_hall';
}

/** The layout revision a building is planned at: a pack interior follows its PIN (no row = 0 = today's plan; a
 *  revision this client does not know is planned at the newest it does); anything else has no pin. */
export function layoutFor(id: string, key: string, pin: LayoutPin | null | undefined): number {
  if (!isPackInterior(id, key)) return currentLayoutRevision();
  const r = pin ? Math.floor(Number(pin.layoutRevision)) : 0;
  return Number.isFinite(r) && r > 0 ? Math.min(r, CURRENT_LAYOUT_REVISION) : 0;
}

// ─── Today's dispatch, named ─────────────────────────────────────────────────────────────────────────────────────────

/** The furnisher `BuildingInteriorModule.enter` has always picked — the SAME tests in the SAME order (the lever's path). */
export type LegacyFurnisher = 'home' | 'guild' | 'hive' | 'pack' | 'purpose';
export function legacyFurnisherFor(id: string, key: string): LegacyFurnisher {
  if (id.startsWith('settle:') || id.startsWith('preset:')) return 'home';
  if (key === 'deed:guild_hall') return 'guild';
  if (key === 'hive:store') return 'hive';
  if (id.startsWith('deed:')) return 'pack';
  return 'purpose';
}

/** Which furnisher draws the room once its descriptor is known: the DESCRIPTOR, except a bespoke room (the hall, the
 *  Hive store) whose archetype is still `legacy.*` — `purposePlan` knows nothing of its columns or its casework. */
export function furnishRouteFor(id: string, key: string, plannerOn: boolean, d: Pick<InteriorDescriptor, 'recipe'> | null): 'descriptor' | LegacyFurnisher {
  const legacy = legacyFurnisherFor(id, key);
  if (!plannerOn || !d) return legacy;
  if ((legacy === 'guild' || legacy === 'hive') && d.recipe.algorithm === 'legacy') return legacy;
  return 'descriptor';
}

// ─── The plan input, the cache ───────────────────────────────────────────────────────────────────────────────────────

export interface WiringBuilding { id: string; key: string; w: number; h: number; d: number }

export type WiringTradition = 'aurelian' | 'borean' | 'neutral';
export const WIRING_TRADITIONS: readonly WiringTradition[] = ['aurelian', 'borean', 'neutral'];

/** The planner's input for a building at a layout. `tradition` = the LOOK's resolved tradition, for EVERY namespace
 *  (C2: a deed's furniture follows its tradition too) — except at layout 0, the legacy plan, which takes none: a room
 *  with no pin row is today's room and today's keys whatever its city flies. `undefined` = the planner's default. */
export function planInputFor(b: WiringBuilding, purpose: InteriorPurpose, layout: number, tradition?: WiringTradition): PlanInput {
  // THE DEED FRAME LAW (slice G): from THE ROOMS REVISION on a deed's room is planned in the shared footprint table's
  // box — the server's only box — so both sides plan ONE room (interior_rooms.ts). Below it, and wherever the table
  // does not agree with the model this client measured, the measured box plans as it always did.
  const type = b.key.startsWith('deed:') ? b.key.slice('deed:'.length) : '';
  const box = b.id.startsWith('deed:') && layout >= ROOMS_LAYOUT_REVISION && deedBoxAgrees(type, b) ? deedPlanBox(type) : { w: b.w, h: b.h, d: b.d };
  const input: PlanInput = { buildingId: b.id, key: b.key, w: box.w, h: box.h, d: box.d, purpose, algorithm: layout <= 0 ? 'legacy' : 'planner', options: { layoutRevision: Math.max(0, layout) } };
  if (tradition && layout >= 1) input.tradition = tradition;
  return input;
}

/** The traditions a PINNED room is looked for under, in order: the resolved one, the planner's default (`undefined` —
 *  what a pin stamped before C2 was planned with), then the others. The first whose plan carries the pin's digest IS
 *  the room; none → today's plan at the resolved tradition, with drift. */
export function pinnedTraditionOrder(resolved: WiringTradition | undefined): Array<WiringTradition | undefined> {
  const out: Array<WiringTradition | undefined> = [];
  for (const t of [resolved, undefined, ...WIRING_TRADITIONS]) if (!out.includes(t)) out.push(t);
  return out;
}
/** Does the visit have a digest to find? (a pin at revision ≥ 1 that carries one) */
export function pinHasDigest(pin: LayoutPin | null | undefined): boolean {
  return !!pin && pin.layoutRevision >= 1 && DIGEST_RE.test(pin.digest);
}

const mmr = (v: number): number => Math.round(v * 1000);
/** Every input the plan depends on, plus the versions of the data it is cut from (an HMR'd catalog must not serve a stale room). */
export function planKey(input: PlanInput): string {
  const a = archetypeFor(input.key, input.purpose);
  const o = input.options ?? {};
  return [
    input.buildingId, input.key, mmr(input.w), mmr(input.h), mmr(input.d), input.purpose, input.tradition ?? '', input.algorithm ?? 'planner',
    o.layoutRevision ?? '', o.stairZone ?? '', o.tiers ?? '', o.archetype ?? '', o.K ?? '', o.R ?? '', input.ownedRevision ?? '', (input.owned?.length ?? 0),
    `${a.id}@${a.revision}`, KIT_VERSION, Object.keys(FURNITURE).length,
  ].join('|');
}

export const PLAN_CACHE_BUDGET_BYTES = 12 * 1024 * 1024;
interface CacheEntry { result: PlanResult; bytes: number }
const planCache = new Map<string, CacheEntry>(); // insertion order = recency (re-inserted on a hit)
let planCacheBytes = 0;
const cacheStats = { hits: 0, misses: 0, evictions: 0, plans: 0, planMs: 0, worstMs: 0 };
let planCacheBudget = PLAN_CACHE_BUDGET_BYTES;

/** A descriptor's size in memory, ESTIMATED from its counts (a JSON pass over a tower would cost what the cache saves). */
export function estimatePlanBytes(r: PlanResult): number {
  let n = 2048;
  for (const f of r.descriptor.floors) {
    n += 512 + f.pieces.length * 420 + f.zones.length * 360 + f.anchors.length * 380 + f.units.length * 300 + f.routeNodes.length * 120;
    for (const e of f.routes) n += 96 + e.points.length * 40;
    for (const z of f.zones) n += z.rects.length * 64;
  }
  n += r.report.candidates.length * 320 + r.report.reserved.length * 160 + r.descriptor.links.length * 140;
  return n;
}

export function planCachePeek(input: PlanInput): PlanResult | null {
  const k = planKey(input);
  const hit = planCache.get(k);
  if (!hit) return null;
  planCache.delete(k); planCache.set(k, hit); // touch
  cacheStats.hits++;
  return hit.result;
}

export function planCachePut(input: PlanInput, result: PlanResult, ms = 0): void {
  const k = planKey(input);
  const old = planCache.get(k);
  if (old) { planCacheBytes -= old.bytes; planCache.delete(k); }
  const bytes = estimatePlanBytes(result);
  planCache.set(k, { result, bytes });
  planCacheBytes += bytes;
  cacheStats.plans++; cacheStats.planMs += ms; if (ms > cacheStats.worstMs) cacheStats.worstMs = ms;
  // evict the least recently used — never the entry just written, whatever its size
  for (const [key, e] of planCache) {
    if (planCacheBytes <= planCacheBudget || planCache.size <= 1) break;
    if (key === k) continue;
    planCache.delete(key); planCacheBytes -= e.bytes; cacheStats.evictions++;
  }
}

/** Plan ONCE per input (synchronous — the worker path fills the same cache through `planCachePut`). */
export function planCached(input: PlanInput): PlanResult {
  const hit = planCachePeek(input);
  if (hit) return hit;
  cacheStats.misses++;
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const result = planInterior(input);
  planCachePut(input, result, (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);
  return result;
}

export function planCacheStats(): { entries: number; bytes: number; budget: number; hits: number; misses: number; evictions: number; plans: number; planMs: number; worstMs: number } {
  return { entries: planCache.size, bytes: planCacheBytes, budget: planCacheBudget, ...cacheStats, planMs: Math.round(cacheStats.planMs * 10) / 10, worstMs: Math.round(cacheStats.worstMs * 10) / 10 };
}
/** Harness / probe lever: empty the cache; optionally set the budget. */
export function planCacheReset(budgetBytes?: number): void {
  planCache.clear(); planCacheBytes = 0;
  cacheStats.hits = cacheStats.misses = cacheStats.evictions = cacheStats.plans = 0; cacheStats.planMs = cacheStats.worstMs = 0;
  planCacheBudget = budgetBytes ?? PLAN_CACHE_BUDGET_BYTES;
}

/** Is this plan worth a worker? The floors × the core's area (m²): a house plans in a few ms, a tower in hundreds. */
export const PLAN_HEAVY_M2 = 500;
export function planIsHeavy(spec: { hw: number; hd: number; floors: number }): boolean {
  return spec.floors * (2 * spec.hw) * (2 * spec.hd) > PLAN_HEAVY_M2;
}

// ─── The detail rule ─────────────────────────────────────────────────────────────────────────────────────────────────

export type DetailLevel = 0 | 1 | 2;

/** What may be hidden at all: a DETAIL-tier piece that does nothing (a lamp's glow toggle is not a function anyone
 *  loses), that no anchor names and that nothing stands on or needs. Never a primary / secondary / owned piece, a
 *  seat, storage, an opener, a planter, a guest book. */
function hideable(p: DescPiece, anchored: ReadonlySet<string>, supports: ReadonlySet<string>): boolean {
  if (p.tier !== 'detail') return false;
  const fn = FURNITURE_FUNCTION[p.key];
  if (fn && fn !== 'toggle') return false;
  if (anchored.has(p.slot) || supports.has(p.slot)) return false;
  return true;
}

/** The slots a floor's detail level HIDES. 0 = every hideable piece; 1 = the hideable pieces that block walking (the
 *  small lived-in pieces stay — a lamp on a table, a rug); 2 (`detailDecor`) = nothing. */
export function hiddenSlotsAtDetail(floor: Pick<DescFloor, 'pieces' | 'anchors'>, level: DetailLevel, among?: ReadonlySet<string>): Set<string> {
  const out = new Set<string>();
  if (INTERIOR_DETAIL_RULES[level].detailDecor) return out; // slice D's flag, read here: the level that draws the decor draws everything
  const anchored = new Set<string>();
  for (const a of floor.anchors) if (a.piece) anchored.add(a.piece);
  const pieces = among ? floor.pieces.filter((p) => among.has(p.slot)) : floor.pieces;
  // a support is kept while what stands on it (or needs it) is kept: resolve to a fixed point
  for (const p of pieces) if (hideable(p, anchored, new Set()) && (level === 0 || p.blocksWalk)) out.add(p.slot);
  for (let guard = 0; guard < 4; guard++) {
    let changed = false;
    for (const p of pieces) {
      if (out.has(p.slot)) continue;
      for (const dep of [...(p.on ? [p.on] : []), ...(p.needs ?? [])]) if (out.delete(dep)) changed = true;
    }
    if (!changed) break;
  }
  return out;
}
export function pieceShownAtDetail(floor: Pick<DescFloor, 'pieces' | 'anchors'>, slot: string, level: DetailLevel): boolean {
  return !hiddenSlotsAtDetail(floor, level).has(slot);
}

// ─── The seat view (what the visit seats) ────────────────────────────────────────────────────────────────────────────

/** Walk-blocking pieces that get a collider on one floor, in priority order (a hall of forty tables is forty boxes). */
export const FURNITURE_COLLIDER_CAP = 40;

export interface SeatPiece {
  slot: string; key: string; floor: number;
  /** Building-local metres; `yaw` in the plans' convention (`rotation.y = buildingYaw + yaw`). */
  x: number; z: number; yaw: number;
  /** Metres above the building's ground the piece's base stands at: the floor's slab + the support's top. */
  y: number;
  tier: PieceTier;
  /** The piece's place in the floor's seated list — the legacy `furnIndex` tag. */
  index: number;
  /** `pack:<floor>:<n>:<key>` on a pack interior (law 41's storage identity), else null. */
  storageKey: string | null;
  /** THE LIVE STORAGE (C2): this storage piece / planter IS one the server has room for (`liveStorageKeys`). False on
   *  a keyed piece = plain furniture: no storage prompt, unless a container already stands behind its key. */
  storageLive: boolean;
  /** False = the detail level hides it (never seated, never collides). */
  shown: boolean;
  /** A box collider is added when it is seated: a PLANNED floor's walk-blocking piece, shown, inside the cap. */
  collide: boolean;
  blocksWalk: boolean;
  /** The floor's source: a `legacy` floor is today's plan untouched (no collider, centre-only offers). */
  source: 'planned' | 'legacy';
}
export interface SeatFloor { floor: number; floorY: number; source: DescFloor['source']; pieces: SeatPiece[] }
export interface SeatView {
  floors: SeatFloor[];
  /** Counts over the whole view. */
  pieces: number; shown: number; colliders: number; hidden: number;
  /** Pieces whose key the visit cannot draw (not in the furniture catalog, not one of slice F's procedural props — a
   *  WANTED item with no stand-in yet): never shown, never solid, never a seat. Counted apart from `hidden`. */
  undrawn: number;
  /** Every storage key the view seats (shown pieces only — a hidden piece is never storage). */
  storageKeys: Set<string>;
}

/**
 * What the visit seats for a descriptor. `pack`: `undefined` = not a pack interior (every generated piece of every
 * floor); `null` = a pack interior with no pack bought (EMPTY); a pack = its prefix (`packView` — the same two
 * functions today's `packPlan` uses, so a layout-0 view IS today's `packPlan` slot for slot, key for key).
 */
export function seatView(d: InteriorDescriptor, opts: { pack?: FurnishPack | null; detail?: DetailLevel }): SeatView {
  const detail = opts.detail ?? 2;
  const floors = [...d.floors].sort((a, b) => a.floor - b.floor);
  const packed = opts.pack === undefined ? null : opts.pack ? packView(d, opts.pack) : [];
  const out: SeatView = { floors: [], pieces: 0, shown: 0, colliders: 0, hidden: 0, undrawn: 0, storageKeys: new Set() };
  const live = packed === null ? null : liveStorageKeys(d);
  floors.forEach((f, fi) => {
    const list: Array<{ piece: DescPiece; storageKey: string | null }> = packed === null
      ? f.pieces.filter((p) => p.tier !== 'owned').sort((a, b) => a.order - b.order).map((piece) => ({ piece, storageKey: null }))
      : (packed[fi] ?? []).map((pp) => ({ piece: pp.piece, storageKey: pp.storageKey }));
    const among = new Set(list.map((e) => e.piece.slot));
    const hidden = hiddenSlotsAtDetail(f, detail, among);
    const source: SeatPiece['source'] = f.source === 'planned' ? 'planned' : 'legacy';
    let colliders = 0;
    const pieces: SeatPiece[] = list.map(({ piece, storageKey }, index) => {
      const drawable = drawableKey(piece.key);
      if (!drawable) out.undrawn++;
      const shown = drawable && !hidden.has(piece.slot);
      const collide = shown && source === 'planned' && piece.blocksWalk && colliders < FURNITURE_COLLIDER_CAP;
      if (collide) colliders++;
      if (shown && storageKey) out.storageKeys.add(storageKey);
      const storageLive = !!storageKey && !!live && live.has(storageKey);
      return { slot: piece.slot, key: piece.key, floor: f.floor, x: piece.x, z: piece.z, yaw: piece.yaw, y: f.floorY + piece.lift, tier: piece.tier, index, storageKey, storageLive, shown, collide, blocksWalk: piece.blocksWalk, source };
    });
    out.floors.push({ floor: f.floor, floorY: f.floorY, source: f.source, pieces });
    out.pieces += pieces.length; out.colliders += colliders;
    for (const p of pieces) { if (p.shown) out.shown++; else if (hidden.has(p.slot)) out.hidden++; }
  });
  return out;
}

// ─── THE LIVE STORAGE (C2 item 4) ────────────────────────────────────────────────────────────────────────────────────

/** A MIRROR of the server's `PLANTERS_PER_STRUCTURE` (`reducers/furniture.ts`; the gate pins it): crops growing at once. */
export const PLANTERS_PER_STRUCTURE = 6;

/** The keyed function of a pack piece — the two the server keeps a row for behind `pack:<floor>:<n>:<key>`. */
export function keyedFunctionOf(key: string): 'storage' | 'garden' | null {
  const f = FURNITURE_FUNCTION[key];
  return f === 'storage' || f === 'garden' ? f : null;
}

const liveCache = new WeakMap<InteriorDescriptor, Set<string>>();
/**
 * WHICH storage pieces and planters of a pack room are LIVE — a function of the descriptor alone, so every client, the
 * console and the gate agree, and nothing a player does re-deals it. The server's caps stay (16 pack containers a
 * structure, 6 of a kind, 6 crops): pieces are taken in the order a buyer meets them — the SMALLEST PACK that seats
 * the piece, then the floor, then its place in the floor's order — until a cap is full. Because a pack is a prefix of
 * the next, the live pieces of pack p are exactly the live pieces of the full room that p seats: buying a bigger pack
 * adds live storage, never moves it. The detail pass hides no functional piece, so the mark is the same at every
 * level. A LEGACY floor (layout 0: today's room) is all live, as today — the dynamic mirror (`keyedOffer`) is its
 * only limit — and counts toward the caps of any planned floor beside it.
 */
export function liveStorageKeys(d: InteriorDescriptor): Set<string> {
  const hit = liveCache.get(d);
  if (hit) return hit;
  const floors = [...d.floors].sort((a, b) => a.floor - b.floor);
  const first = new Map<string, { kind: string; fn: 'storage' | 'garden'; floor: number; index: number; pack: number; planned: boolean }>();
  for (const pack of [...FURNISH_PACKS].sort((a, b) => a.pack - b.pack)) {
    packView(d, pack).forEach((list, fi) => list.forEach((pp, index) => {
      const fn = keyedFunctionOf(pp.piece.key);
      if (!fn || first.has(pp.storageKey)) return;
      first.set(pp.storageKey, { kind: pp.piece.key, fn, floor: floors[fi].floor, index, pack: pack.pack, planned: floors[fi].source === 'planned' });
    }));
  }
  const live = new Set<string>();
  let all = 0, crops = 0;
  const ofKind = new Map<string, number>();
  const take = (key: string, e: { kind: string; fn: 'storage' | 'garden' }): void => {
    live.add(key);
    if (e.fn === 'garden') crops++; else { all++; ofKind.set(e.kind, (ofKind.get(e.kind) ?? 0) + 1); }
  };
  const entries = [...first.entries()];
  for (const [key, e] of entries) if (!e.planned) take(key, e); // today's room: every piece, as today
  const planned = entries.filter(([, e]) => e.planned).sort(([ka, a], [kb, b]) => a.pack - b.pack || a.floor - b.floor || a.index - b.index || (ka < kb ? -1 : 1));
  for (const [key, e] of planned) {
    if (e.fn === 'garden') { if (crops < PLANTERS_PER_STRUCTURE) take(key, e); continue; }
    if (all < PACK_CONTAINERS_PER_STRUCTURE && (ofKind.get(e.kind) ?? 0) < PACK_CONTAINERS_PER_KIND) take(key, e);
  }
  liveCache.set(d, live);
  return live;
}

/**
 * THE E ARBITER'S RULE for a pack piece's keyed function: is it OFFERED at all? A piece whose row already exists
 * always is (what is stored stays reachable, whatever the mark says). Otherwise it must be LIVE and the server must
 * have room — its own rule, word for word (`packStorageOpens`; crops < 6). Not offered = plain furniture: no prompt,
 * no "full" wording, nothing that can be refused. `containers` / `planters` = the structure's rows this client sees
 * (the owner's; a stranger sees none and is told a live piece is the owner's storage).
 */
export function keyedOffer(a: {
  fn: 'storage' | 'garden'; key: string; kind: string; live: boolean;
  containers: ReadonlyArray<{ key: string; kind: string }>; planters: ReadonlyArray<string>;
}): boolean {
  if (a.fn === 'storage') return a.containers.some((c) => c.key === a.key) || (a.live && packStorageOpens(a.containers, a.key, a.kind));
  return a.planters.includes(a.key) || (a.live && a.planters.length < PLANTERS_PER_STRUCTURE);
}

/** How many pieces each pack seats in a descriptor's room — what the console prints beside each price. */
export function packCountsOf(d: InteriorDescriptor): Array<{ pack: FurnishPack; items: number }> {
  return FURNISH_PACKS.map((pack) => ({ pack, items: packView(d, pack).reduce((a, fl) => a + fl.length, 0) }));
}

/** The pack containers / planters a structure holds whose piece the room no longer seats (a planner change under a
 *  pin, a lever flip, a refit): the console lists them so their contents can always be taken out. */
export function unseatedPackKeys(keys: Iterable<string>, seated: ReadonlySet<string>): string[] {
  const out: string[] = [];
  for (const k of keys) if (k.startsWith('pack:') && !seated.has(k)) out.push(k);
  return out.sort();
}

// ─── A seated piece's collider (the DRAWN bounds) ────────────────────────────────────────────────────────────────────

/** The thinnest a furniture collider may be (m): a 0.3 m capsule must not depenetrate through a statue's plinth. */
export const FURNITURE_COLLIDER_MIN_HALF = 0.2;

/**
 * The box collider of a seated piece from the model's OWN fitted bounds: `min` / `max` are the master's bounding box,
 * `scale` / `yOff` what `furnitureFit` returned — exactly what is drawn (the catalog bound `piece.half` is up to 40 %
 * wider on two axes). Returned in the building's frame: centre (lx, ly, lz), half extents, yaw (the plans' convention).
 */
export function pieceColliderBox(
  p: { x: number; z: number; yaw: number; y: number },
  fit: { scale: number; yOff: number; min: readonly [number, number, number]; max: readonly [number, number, number] },
): { lx: number; ly: number; lz: number; hx: number; hy: number; hz: number; yaw: number } {
  const s = fit.scale;
  const cx = ((fit.min[0] + fit.max[0]) / 2) * s, cz = ((fit.min[2] + fit.max[2]) / 2) * s;
  const c = Math.cos(p.yaw), sn = Math.sin(p.yaw);
  const hy = Math.max(0.05, ((fit.max[1] - fit.min[1]) * s) / 2);
  return {
    lx: p.x + cx * c + cz * sn, lz: p.z - cx * sn + cz * c, // three's rotation.y — the visit's toWorld
    ly: p.y + fit.yOff + fit.min[1] * s + hy,
    hx: Math.max(FURNITURE_COLLIDER_MIN_HALF, ((fit.max[0] - fit.min[0]) * s) / 2),
    hy,
    hz: Math.max(FURNITURE_COLLIDER_MIN_HALF, ((fit.max[2] - fit.min[2]) * s) / 2),
    yaw: p.yaw,
  };
}

/**
 * CRAFTED PIECES COLLIDE TOO (C2 item 3): the box of a placed `structure_item` in its STRUCTURE's frame, from the same
 * fitted bounds — exactly what `DecorationModule` draws (the row's lx / ly / lz, `yOff` added to the position, the
 * rotation three's Euler(pitch, yaw, roll) 'XYZ', then the fit scale). A piece turned about Y alone gets the oriented
 * box a planned piece gets; one that was pitched or rolled gets the structure-aligned box that holds all eight corners.
 */
export function itemColliderBox(
  item: { lx: number; ly: number; lz: number; pitch: number; yaw: number; roll: number },
  fit: { scale: number; yOff: number; min: readonly [number, number, number]; max: readonly [number, number, number] },
): { lx: number; ly: number; lz: number; hx: number; hy: number; hz: number; yaw: number } {
  if (Math.abs(item.pitch) < 1e-3 && Math.abs(item.roll) < 1e-3) return pieceColliderBox({ x: item.lx, z: item.lz, yaw: item.yaw, y: item.ly }, fit);
  const a = Math.cos(item.pitch), b = Math.sin(item.pitch), c = Math.cos(item.yaw), d = Math.sin(item.yaw), e = Math.cos(item.roll), f = Math.sin(item.roll);
  // three's Matrix4.makeRotationFromEuler, order XYZ
  const m = [c * e, -c * f, d, a * f + b * e * d, a * e - b * f * d, -b * c, b * f - a * e * d, b * e + a * f * d, a * c];
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (let i = 0; i < 8; i++) {
    const vx = (i & 1 ? fit.max[0] : fit.min[0]) * fit.scale, vy = (i & 2 ? fit.max[1] : fit.min[1]) * fit.scale, vz = (i & 4 ? fit.max[2] : fit.min[2]) * fit.scale;
    const x = m[0] * vx + m[1] * vy + m[2] * vz, y = m[3] * vx + m[4] * vy + m[5] * vz, z = m[6] * vx + m[7] * vy + m[8] * vz;
    x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y); z0 = Math.min(z0, z); z1 = Math.max(z1, z);
  }
  return {
    lx: item.lx + (x0 + x1) / 2, ly: item.ly + fit.yOff + (y0 + y1) / 2, lz: item.lz + (z0 + z1) / 2,
    hx: Math.max(FURNITURE_COLLIDER_MIN_HALF, (x1 - x0) / 2), hy: Math.max(0.05, (y1 - y0) / 2), hz: Math.max(FURNITURE_COLLIDER_MIN_HALF, (z1 - z0) / 2), yaw: 0,
  };
}

/** A crafted piece that gets a collider: a `furn_<key>` prefab whose key the kit table marks `blocksWalk`. */
export function itemBlocksWalk(prefabId: string): string | null {
  if (!prefabId.startsWith('furn_')) return null;
  const key = prefabId.slice(5);
  return pieceMeta(key)?.blocksWalk ? key : null;
}

/** The storey a height above the building's ground belongs to (a crafted piece's `ly`; the collider cap is per floor). */
// ─── THE PARTITIONS IN THE VISIT, AND WHO IS WHERE (slice G, 2026-10-01) ────────────────────────────────────────────

/** A doorway's head (m): above it the wall carries on (a header), below it the opening is framed and OPEN. */
export const DOOR_HEAD_M = 2.15;
/** One box of a partition as the visit builds it — building-local metres, `y` the CENTRE above the building's ground. */
export interface PartitionBox { floor: number; x: number; y: number; z: number; w: number; h: number; d: number; role: 'panel' | 'frame'; collide: boolean }
/**
 * What the visit builds for a descriptor's partitions: the wall's solid spans (THICK boxes, drawn and collided at the
 * partition's own 0.4 m — interiors.md law 7), and round every doorway a header above the head and a frame (two jambs,
 * a lintel) — an OPEN doorway, never a leaf. A screen is its low panel and a cap. Only the solid spans collide.
 */
export function partitionBoxesFor(d: InteriorDescriptor): PartitionBox[] {
  const out: PartitionBox[] = [];
  for (const f of d.floors) {
    const base = f.floor === 0 ? 0 : f.floor * FLOOR_H;
    for (const w of f.partitions) {
      const alongX = Math.abs(w.x1 - w.x0) >= Math.abs(w.z1 - w.z0);
      const T = w.thickness, H = w.height, low = H < 2.4;
      for (const q of partitionBoxes(w)) {
        out.push({ floor: f.floor, x: (q.x0 + q.x1) / 2, y: base + H / 2, z: (q.z0 + q.z1) / 2, w: q.x1 - q.x0, h: H, d: q.z1 - q.z0, role: 'panel', collide: true });
        if (low) out.push({ floor: f.floor, x: (q.x0 + q.x1) / 2, y: base + H + 0.025, z: (q.z0 + q.z1) / 2, w: q.x1 - q.x0 + 0.04, h: 0.05, d: q.z1 - q.z0 + 0.04, role: 'frame', collide: false });
      }
      if (low) continue;
      const at = (s: number): { x: number; z: number } => (alongX ? { x: w.x0 + s, z: w.z0 } : { x: w.x0, z: w.z0 + s });
      const F = 0.08, P = T + 0.06;
      for (const [a0, a1] of w.apertures) {
        const m = (a0 + a1) / 2, len = a1 - a0, hh = H - DOOR_HEAD_M;
        if (hh > 0.05) out.push({ floor: f.floor, ...at(m), y: base + DOOR_HEAD_M + hh / 2, w: alongX ? len : T, h: hh, d: alongX ? T : len, role: 'panel', collide: false });
        for (const s of [a0 + F / 2, a1 - F / 2]) out.push({ floor: f.floor, ...at(s), y: base + DOOR_HEAD_M / 2, w: alongX ? F : P, h: DOOR_HEAD_M, d: alongX ? P : F, role: 'frame', collide: false });
        out.push({ floor: f.floor, ...at(m), y: base + DOOR_HEAD_M - F / 2, w: alongX ? len : P, h: F, d: alongX ? P : len, role: 'frame', collide: false });
      }
    }
  }
  return out;
}

/** Which buildings the server keeps presence for: a deed (its room plan is mirrored there). */
export function presenceReportable(id: string): boolean { return /^deed:\d+$/.test(id); }
/** A room change is reported once it has held this long (ms); a refused report is retried after this long. */
export const PRESENCE_DEBOUNCE_MS = 350;
export const PRESENCE_RETRY_MS = 2000;
/** The floor and room a body stands in: its room of the descriptor's plan (the nearest when it touches a wall), or —
 *  a room with no plan (the lever, a bespoke furnisher) — the floor's one room. `digest` goes with the report. */
export function visitRoomAt(d: InteriorDescriptor | null, floors: number, lx: number, ly: number, lz: number): { floor: number; roomId: string; digest: string } {
  const floor = floorOfHeight(ly, floors);
  const fl = d?.floors.find((f) => f.floor === floor);
  const room = fl ? (roomAt(fl, lx, lz) ?? roomAt(fl, lx, lz, 1e6)) : null;
  return { floor, roomId: room?.id ?? roomIdOf(floor, 'main'), digest: d?.digest ?? '' };
}

export function floorOfHeight(ly: number, floors: number): number {
  return Math.max(0, Math.min(Math.max(1, floors) - 1, Math.floor((ly + 0.3) / FLOOR_H)));
}

/**
 * WHICH crafted pieces get a box, deterministically: lowest item id first, each floor sharing `FURNITURE_COLLIDER_CAP`
 * with the planned pieces already solid there (`plannedOnFloor`). Returns the item ids (as strings) that collide.
 */
export function itemColliderPicks(
  items: ReadonlyArray<{ id: bigint; prefabId: string; ly: number }>, floors: number, plannedOnFloor: (floor: number) => number,
): Array<{ id: string; key: string; floor: number }> {
  const used = new Map<number, number>();
  const out: Array<{ id: string; key: string; floor: number }> = [];
  for (const it of [...items].sort((p, q) => (p.id < q.id ? -1 : p.id > q.id ? 1 : 0))) {
    const key = itemBlocksWalk(it.prefabId);
    if (!key) continue;
    const floor = floorOfHeight(it.ly, floors);
    const n = (used.get(floor) ?? 0) + plannedOnFloor(floor);
    if (n >= FURNITURE_COLLIDER_CAP) continue;
    used.set(floor, (used.get(floor) ?? 0) + 1);
    out.push({ id: it.id.toString(), key, floor });
  }
  return out;
}

// ─── THE LAST-RESORT GROUND AND THE INDOOR RESCUE (C2 item 2) ────────────────────────────────────────────────────────

/** The lever: `localStorage atlas:interior-safety = 'off'` → no last-resort ground, no rescue (the visit as it was). */
export const SAFETY_LEVER_KEY = 'atlas:interior-safety';
export function interiorSafetyEnabled(read?: (k: string) => string | null): boolean {
  try {
    const get = read ?? ((k: string) => (typeof localStorage === 'undefined' ? null : localStorage.getItem(k)));
    return get(SAFETY_LEVER_KEY) !== 'off';
  } catch { return true; }
}
/** Interiors that already carry their own ground of last resort (`hive:ground`, `<preset>:safety` — interiors.md §4.6). */
export function ownsLastResortGround(id: string, worldKind: string | null | undefined): boolean {
  return id.startsWith('preset:') || worldKind === 'merchant';
}
/**
 * THE GROUND-OF-LAST-RESORT LAW, for every interior (it was the Hive's and the presets'): a box 2 m thick under the
 * ground floor, its top 10 cm under the pad — never at the pad's own height (two ground authorities at one height
 * oscillate). It reaches as far as a visit lasts (`reach` past the walkable core — the walked-out margin), so the door
 * corridor has a floor while a restamp rebuilds the chunk's trimesh under it. Building-local, centre y from groundY.
 */
export const SAFETY_TOP_M = -0.1, SAFETY_HALF_H = 1, SAFETY_REACH_M = 1.9;
export function safetyGroundBox(spec: { hw: number; hd: number }): { hx: number; hy: number; hz: number; y: number } {
  return { hx: spec.hw + SAFETY_REACH_M, hy: SAFETY_HALF_H, hz: spec.hd + SAFETY_REACH_M, y: SAFETY_TOP_M - SAFETY_HALF_H };
}
/** Under the ground floor by this much, inside the room's footprint, for this long → the capsule is wedged below it. */
export const RESCUE_UNDER_M = 0.45, RESCUE_DWELL_MS = 750;
/**
 * THE INDOOR RESCUE's verdict for one frame. `dy` = the feet against the building's ground (m); `inFootprint` = the
 * capsule is UNDER THE ROOM (over the last-resort ground's footprint — outside it the visit is ending: the scan exits
 * and the world's own clamp takes the capsule back); `underMs` = how long it has been more than `RESCUE_UNDER_M` under
 * the ground floor. More than a storey under the floor → at once (nothing legal is down there: the terrain clamp
 * stands down indoors, so nothing else would ever bring it back); wedged under the slab → after the dwell.
 */
export function rescueVerdict(dy: number, inFootprint: boolean, underMs: number): 'fell' | 'wedged' | null {
  if (!Number.isFinite(dy) || !inFootprint) return null;
  if (dy < -FLOOR_H) return 'fell';
  if (dy < -RESCUE_UNDER_M && underMs >= RESCUE_DWELL_MS) return 'wedged';
  return null;
}

// ─── Slice F's procedural props and shell fixtures, as the visit seats them ──────────────────────────────────────────

/** Can the visit DRAW this key: a catalog piece (a served model), or one of slice F's procedural props (boxes). */
export function drawableKey(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(FURNITURE, key) || isKitProp(key);
}

/** How far a riser's floor collider runs below the slab it stands on (m) — law 7: thick, or not at all. */
export const PROP_FLOOR_DOWN_M = 0.8;
/** A walk-on prop no taller than this is a floor MARKING and takes no collider (slice F's contract). */
export const PROP_MARKING_MAX_H = 0.05;

/**
 * A seated PROP's collider from its own boxes (`propBounds` — a prop draws at exactly its size, no fit):
 *   · a `walkOn` prop (a riser ≤ 0.4 m) is a FLOOR: a box whose top is the prop's top, its body running
 *     `PROP_FLOOR_DOWN_M` below the slab — unless it is a floor MARKING (≤ `PROP_MARKING_MAX_H`), which has none;
 *   · a `blocksWalk` prop is a SOLID like any furniture (`collide` — the seat view's per-floor cap applies);
 *   · anything else (a floor marking, a see-through screen a body passes) has none.
 */
export function propColliderBox(
  p: { x: number; z: number; yaw: number; y: number }, key: string, collide: boolean,
): { lx: number; ly: number; lz: number; hx: number; hy: number; hz: number; yaw: number; kind: 'floor' | 'solid' } | null {
  const spec = kitProp(key);
  if (!spec) return null;
  const b = propBounds(spec);
  if (spec.walkOn) {
    if (b.y1 <= PROP_MARKING_MAX_H) return null; // a floor marking (a service mat, a painted line): the slab under it is the floor
    const c = Math.cos(p.yaw), sn = Math.sin(p.yaw);
    const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2, hy = (b.y1 + PROP_FLOOR_DOWN_M) / 2;
    return { lx: p.x + cx * c + cz * sn, lz: p.z - cx * sn + cz * c, ly: p.y + b.y1 - hy, hx: (b.x1 - b.x0) / 2, hy, hz: (b.z1 - b.z0) / 2, yaw: p.yaw, kind: 'floor' };
  }
  if (!collide) return null;
  return { ...pieceColliderBox(p, { scale: 1, yOff: 0, min: [b.x0, b.y0, b.z0], max: [b.x1, b.y1, b.z1] }), kind: 'solid' };
}

/** A FIXTURE WITH A MODEL (C2 item 8; slice N's `FIXTURE_MODEL`): the lever. `localStorage atlas:interior-fixture-models
 *  = 'off'` keeps the procedural stand-in (as does the look's own lever — the model is lit by slice D's adapter). */
export const FIXTURE_MODEL_LEVER_KEY = 'atlas:interior-fixture-models';
export function fixtureModelsEnabled(read?: (k: string) => string | null): boolean {
  try {
    const get = read ?? ((k: string) => (typeof localStorage === 'undefined' ? null : localStorage.getItem(k)));
    return get(FIXTURE_MODEL_LEVER_KEY) !== 'off' && interiorLookEnabled(get);
  } catch { return true; }
}
/** Where a fixture's model clone stands in the building's frame (slice N's rule: `fixtureModelSeat` turned by the
 *  fixture's yaw), given the fixture's seat, its storey's nominal level and the fit's base lift. */
export function fixtureModelPose(fx: { x: number; y: number; z: number; yaw: number }, floorY: number, seat: { dy: number; dz: number }, yOff: number): { x: number; y: number; z: number; yaw: number } {
  return { x: fx.x + Math.sin(fx.yaw) * seat.dz, y: floorY + fx.y + seat.dy + yOff, z: fx.z + Math.cos(fx.yaw) * seat.dz, yaw: fx.yaw };
}

/** Which rooms wear ceiling light panels, and which a sconce either side of the door — `shellFixtures`' options by
 *  purpose. (Slice F's plan bed holds the same two sets; this is the visit's copy until F's library exports one.) */
const CEILING_LIGHT_PURPOSES: ReadonlySet<string> = new Set(['clinic', 'workshop', 'civic', 'bank', 'transit', 'office', 'garage', 'retail']);
const SCONCE_PURPOSES: ReadonlySet<string> = new Set(['residence', 'apartment', 'lodge', 'hospitality', 'guild', 'theater']);
export function fixtureOptionsFor(purpose: string): FixtureOptions {
  return { ceilingLights: CEILING_LIGHT_PURPOSES.has(purpose), doorSconces: SCONCE_PURPOSES.has(purpose), floorH: FLOOR_H };
}

/** Does the visit seat shell fixtures for this room: a PLANNED descriptor (a legacy plan is today's room — nothing is
 *  added to it) at detail 1 or 2 (the cheapest tier draws the bare shell). */
export function fixturesShown(d: InteriorDescriptor, detail: DetailLevel): boolean {
  return d.recipe.algorithm === 'planner' && detail >= 1;
}

// ─── Approach points and sockets (the E key meets the anchor) ────────────────────────────────────────────────────────

export interface PieceUse {
  /** Where a user stands to use the piece (building-local) — cells the planner's capsule route reaches. */
  approach: Array<{ x: number; z: number }>;
  /** Where a body sits / lies on it, and the yaw it faces (the plans' convention). */
  sockets: Array<{ pose: 'sit' | 'lie'; x: number; z: number; yaw: number }>;
}

/** A piece's approach points and sockets from its floor's anchors. A LEGACY floor answers nothing (its anchors are
 *  best-effort; today's centre-only offer stands there). */
export function pieceUseOf(d: InteriorDescriptor, floor: number, slot: string): PieceUse {
  const out: PieceUse = { approach: [], sockets: [] };
  const f = d.floors.find((q) => q.floor === floor);
  if (!f || f.source !== 'planned') return out;
  for (const a of f.anchors) {
    if (a.piece !== slot) continue;
    if (!out.approach.some((q) => Math.abs(q.x - a.ax) < 0.05 && Math.abs(q.z - a.az) < 0.05)) out.approach.push({ x: a.ax, z: a.az });
    if (a.seat) out.sockets.push({ pose: a.seat.pose, x: a.seat.x, z: a.seat.z, yaw: a.seat.yaw });
  }
  return out;
}

// ─── Pack storage: the server's room for it ──────────────────────────────────────────────────────────────────────────

/** MIRRORS of `server/src/reducers/furniture.ts` (the gate pins both): how many PACK containers one structure may hold. */
export const PACK_CONTAINERS_PER_KIND = 6;
export const PACK_CONTAINERS_PER_STRUCTURE = 16;

/**
 * Would the server open (create on first use) this pack piece's container? Its rule, word for word: a piece that
 * already HAS a container always opens; a new one needs room — fewer than 16 pack containers in the structure and
 * fewer than 6 of this kind. A museum's planned room seats 22 bookcases and a mine 13 shelves: the E prompt must not
 * offer storage the server will refuse ("This structure holds all the pack storage it may"), so past the caps a
 * storage piece is furniture to look at. `existing` = the structure's container rows the owner can see.
 */
export function packStorageOpens(existing: ReadonlyArray<{ key: string; kind: string }>, key: string, kind: string): boolean {
  if (!key.startsWith('pack:')) return true;
  let all = 0, ofKind = 0;
  for (const c of existing) {
    if (c.key === key) return true;
    if (c.key.startsWith('pack:')) { all++; if (c.kind === kind) ofKind++; }
  }
  return all < PACK_CONTAINERS_PER_STRUCTURE && ofKind < PACK_CONTAINERS_PER_KIND;
}

// ─── The residents' room ─────────────────────────────────────────────────────────────────────────────────────────────

/** Anchor kinds a resting resident may STAND at (public standing points; never a staff side, a bedside or a doorway). */
const STAND_KINDS: ReadonlySet<DescAnchor['kind']> = new Set(['stand', 'seat', 'display', 'service-customer', 'queue', 'perform']);
const STAFF_STAND_KINDS: ReadonlySet<DescAnchor['kind']> = new Set(['stand', 'work', 'service-staff', 'display']);

/**
 * The sockets, standing points and rugs residents take in a room, from what the visit SEATS (a hidden or unpacked
 * piece is no seat). A seat's sockets come from its anchors where the planner resolved them, else from the piece's own
 * metadata (`pieceMeta.sockets`) — so a legacy floor's beds and sofas are sockets too. Lifts are `deed_home`'s.
 */
export function residentRoomOf(d: InteriorDescriptor, view: SeatView): ResidentRoom {
  const room: ResidentRoom = { sockets: [], stands: [], rugs: [], solids: [] };
  const staff: ResidentRoom['stands'] = [];
  for (const sf of view.floors) {
    const f = d.floors.find((q) => q.floor === sf.floor);
    if (!f) continue;
    const shown = new Map(sf.pieces.filter((p) => p.shown).map((p) => [p.slot, p]));
    for (const p of shown.values()) {
      const dp = f.pieces.find((q) => q.slot === p.slot);
      if (dp && p.blocksWalk) {
        const cs = pieceCorners(dp);
        room.solids.push({ floor: sf.floor, x0: Math.min(...cs.map((c) => c[0])), x1: Math.max(...cs.map((c) => c[0])), z0: Math.min(...cs.map((c) => c[1])), z1: Math.max(...cs.map((c) => c[1])) });
      }
      if (p.key === 'rug_woven') { room.rugs.push({ x: p.x, z: p.z, floor: sf.floor }); continue; }
      const fn = FURNITURE_FUNCTION[p.key];
      if (fn !== 'sit' && fn !== 'lie') continue;
      const h = FURNITURE[p.key]?.h ?? 0.5;
      const lift = (p.y - sf.floorY) + (fn === 'lie' ? h * 0.7 : Math.min(0.5, h * 0.55));
      const fromAnchors = f.anchors.filter((a) => a.piece === p.slot && a.seat);
      if (fromAnchors.length > 0) {
        for (const a of fromAnchors) room.sockets.push({ pose: fn, x: a.seat!.x, z: a.seat!.z, yaw: a.seat!.yaw, floor: sf.floor, lift, slot: p.slot });
      } else {
        const c = Math.cos(p.yaw), sn = Math.sin(p.yaw);
        for (const s of pieceMeta(p.key)?.sockets ?? [{ dx: 0 }]) room.sockets.push({ pose: fn, x: p.x + s.dx * c, z: p.z - s.dx * sn, yaw: p.yaw, floor: sf.floor, lift, slot: p.slot });
      }
    }
    if (f.source !== 'planned') continue; // a legacy floor's standing points are not proven free
    for (const a of f.anchors) {
      if (a.piece && !shown.has(a.piece)) continue;
      if (STAND_KINDS.has(a.kind) && a.access === 'public') room.stands.push({ x: a.ax, z: a.az, facing: a.facing, floor: sf.floor });
      else if (STAFF_STAND_KINDS.has(a.kind) && a.access !== 'guarded') staff.push({ x: a.ax, z: a.az, facing: a.facing, floor: sf.floor });
    }
  }
  // A room with no public standing point at all (a workshop, a garage, a watch post: every anchor is the staff's): the
  // household that lives there is its staff — they stand at the bench. Never a guarded point (the vault, the armoury).
  if (room.stands.length === 0) room.stands.push(...staff);
  return room;
}

// ─── The sign's seat ─────────────────────────────────────────────────────────────────────────────────────────────────

/** The window header band's foot (m above the slab) — a MIRROR of `building_interiors.ts`' private `HEADER_H`; the
 *  gate checks the sign against the shell's real boxes and panes. */
export const SIGN_HEADER_Y = 2.7;
export const SIGN_H = 0.475, SIGN_LEGACY_Y = 2.25;

/**
 * Where the nameplate hangs inside the door. `legacy` (the lever) = today's seat: 2.25 m up, inside the window band —
 * across a pane in most rooms. Else ON THE HEADER BAND: the solid strip above the windows (2.7 – 3.4 m), under the
 * ceiling trim, a little proud of the wall. `null` = no wall right of the door to hang it on.
 */
export function signSeat(spec: { hw: number; hd: number }, legacy: boolean): { x: number; y: number; z: number; w: number; h: number } | null {
  const span = doorSignSpan(spec.hw);
  if (!span) return null;
  if (legacy) return { x: span.x, y: SIGN_LEGACY_Y, z: -spec.hd + 0.06, w: span.w, h: SIGN_H * (span.w / SIGN_W) };
  const h = Math.min(SIGN_H * (span.w / SIGN_W), 0.42);
  return { x: span.x, y: SIGN_HEADER_Y + 0.04 + h / 2, z: -spec.hd + 0.1, w: span.w, h };
}

// ─── The active descriptor (what the room the player stands in IS) ───────────────────────────────────────────────────

export interface ActiveInterior {
  /** The enterable's id (`atlas.interiorId`). */
  id: string;
  /** Bumped whenever anything below changes (the plan landed, the pack changed, the pin moved, the detail level). */
  version: number;
  /** The lever at entry. */
  planner: boolean;
  /** `pending` = the plan is still being made (nothing seated yet); `descriptor` = the room is the descriptor's;
   *  a legacy furnisher's name = today's room (the lever, or a bespoke room on a `legacy.*` archetype). */
  route: 'pending' | 'descriptor' | LegacyFurnisher;
  /** The layout it is planned at, and the pin row (pack interiors only; null = no row). */
  layout: number; pin: LayoutPin | null;
  /** The LOOK's resolved tradition (what a fresh plan / a Refit takes), and the one the drawn descriptor was planned
   *  under (a pinned room found under another tradition keeps it until a Refit). null while pending / under the lever. */
  tradition: WiringTradition | null; plannedTradition: WiringTradition | null;
  /** The furnish pack bought (pack interiors), else null. */
  pack: number | null;
  descriptor: InteriorDescriptor | null;
  report: PlanReport | null;
  view: SeatView | null;
  room: ResidentRoom | null;
  /** False = the room is NOT drawn at the structure's pinned layout (the lever on a structure pinned ≥ 1): no keyed
   *  function (storage, the planter) is offered on its pack clones — one layout must never be keyed as another. */
  keyedOk: boolean;
  /** DRIFT: the pin carries a digest and today's plan for this room is not it — the descriptor's digest differs (a
   *  planner / kit / catalog change under the pin), or the room was found under a tradition its city no longer keeps. */
  digestDrift: boolean;
  /** How the plan was made, and how long it took. */
  planVia: 'none' | 'cache' | 'sync' | 'worker'; planMs: number;
}

const G = globalThis as unknown as { __atlasActiveInterior?: ActiveInterior | null };
export function setActiveInterior(a: ActiveInterior | null): void { G.__atlasActiveInterior = a; }
/** THE ACTIVE DESCRIPTOR ACCESSOR: the room the player stands in (null outdoors). Read `version` to know it changed. */
export function activeInterior(): ActiveInterior | null { return G.__atlasActiveInterior ?? null; }
/** The active interior IF it is `id` and the descriptor draws it (what the resident mirror and the E arbiter ask). */
export function activeDescriptorFor(id: string | null | undefined): ActiveInterior | null {
  const a = G.__atlasActiveInterior;
  return a && id && a.id === id && a.route === 'descriptor' && a.descriptor && a.view ? a : null;
}

/** The slab y of a floor, as the furnisher seats it. */
export function floorYOf(floor: number): number { return floor === 0 ? 0.08 : floor * FLOOR_H; }
